import React, { createContext, useContext, useEffect, useState, useCallback, useRef, memo } from "react";
import "./Header.css";
import { useNavigate } from "react-router-dom";
import { FaChevronDown } from "react-icons/fa";
import ProfilePopUp from "./common/ProfilePopUp";
import { useAuth } from "../AuthContext";
import axios from "axios";
import instance, { apiEncryptRequest, currencyReturn, decryptData } from "../Api";

// ─── Dropdown Context ─────────────────────────────────────────────────────────
const DropdownContext = createContext();
export const DropdownProvider = ({ children }) => {
  const [dropdownValue, setDropdownValue] = useState("Global");
  return (
    <DropdownContext.Provider value={{ dropdownValue, setDropdownValue }}>
      {children}
    </DropdownContext.Provider>
  );
};
export const useDropdown = () => useContext(DropdownContext);

// ─── Constants ────────────────────────────────────────────────────────────────
const STATIC_CODES = ["INR", "USD", "GBP"];
const VM_EXPIRE_INTERVAL = 300000;
const isMobileDevice = () => window.matchMedia("(max-width: 1000px)").matches;

// ─── Responsive CSS ───────────────────────────────────────────────────────────
const HEADER_CSS = `
  /* ── Mobile header bar ── */
  .hd-mobile {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 12px;
    height: 60px;
    background: inherit;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
  }

  /* Logo — no negative margins */
  .hd-logo-mobile {
    flex-shrink: 0;
    max-width: 140px;
  }
  .hd-logo-mobile img {
    height: 36px;
    width: auto;
    max-width: 100%;
    object-fit: contain;
    display: block;
  }

  /* Controls row — logged-in */
  .hd-controls {
    display: flex;
    align-items: center;
    gap: 5px;
    flex-shrink: 0;
    overflow: hidden;
  }

  /* Compact selects on mobile */
  .hd-select {
    width: 58px;
    border-radius: 20px;
    padding: 3px 4px;
    border: 2px solid #e97730;
    font-size: 11px;
    background: white;
    color: #333;
    -webkit-appearance: none;
    appearance: none;
    text-align: center;
    cursor: pointer;
  }

  /* Icon button — proper touch target */
  .hd-icon-btn {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    background: #e97730;
    border: 2px solid #e97730;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    flex-shrink: 0;
    -webkit-tap-highlight-color: transparent;
    transition: background 0.15s;
  }
  .hd-icon-btn:active { background: #c45e10; }
  .hd-icon-btn img {
    width: 16px; height: 16px;
    object-fit: contain;
    display: block;
  }

  /* Logged-out currency select */
  .hd-currency-guest {
    border-radius: 20px;
    padding: 6px 10px;
    border: 2px solid #e97730;
    font-size: 13px;
    background: white;
    color: #333;
    cursor: pointer;
    flex-shrink: 0;
  }

  /* ── Responsive web header tweaks ── */
  @media (max-width: 1200px) {
    .header-top .parent-see-width {
      padding: 0 12px;
    }
    .header-top .login-main {
      flex-wrap: wrap;
      gap: 6px;
    }
  }
`;

// ─── Sub-components ───────────────────────────────────────────────────────────
const SelectBox = memo(({ value, onChange, style, children }) => (
  <select name="plan_time" value={value} onChange={onChange} style={style}>
    {children}
  </select>
));

const CurrencyOptions = memo(({ platform }) =>
  platform === 1 ? (
    <>
      <option value="USD">USD</option>
      <option value="INR">INR</option>
      <option value="EUR">EUR</option>
      <option value="GBP">GBP</option>
      <option value="AED">AED</option>
    </>
  ) : (
    <>
      <option value="USD">USD</option>
      <option value="INR">INR</option>
      <option value="GBP">GBP</option>
    </>
  )
);

// Responsive icon button — 36×36px touch target (up from broken 30×30 with 15×13 icon)
const IconCircle = memo(({ src, alt, onClick, style }) => (
  <div className="hd-icon-btn" onClick={onClick} style={style}>
    <img src={src} alt={alt} />
  </div>
));

// ─── Main Header Component ────────────────────────────────────────────────────
const Header = (props) => {
  const navigate = useNavigate();
  const { updateUserDetails, updateCurrencyRate, updateAppCurrency, smuser, isLoginByParentUser, currencyRates, appCurrency } = useAuth();

  const [isMobile,          setIsMobile]          = useState(isMobileDevice);
  const [platform,          setPlatform]          = useState("");
  const [preferCurrency,    setPreferCurrency]    = useState("");
  const [countryCodes,      setCountryCodes]      = useState("");
  const [serverLocation,    setServerLocation]    = useState(null);
  const [selectedLocation,  setSelectedLocation]  = useState("");
  const [popUpType,         setPopUpType]         = useState("");
  const [isPopupOpen,       setIsPopupOpen]       = useState(false);
  const [isClick,           setIsClick]           = useState(false);

  const { dropdownValue, setDropdownValue } = useDropdown();
  const isLogged = !!smuser;

  // ─── API helpers ──────────────────────────────────────────────────────────
  const ChangeCurrency = useCallback(async (value) => {
    if (!smuser) return;
    try {
      const res = await instance.post("/changescurrency", { country: value, user_id: smuser.id });
      const currencyList = res.data.currency1;
      const key = smuser.prefer_currency === "EUR" ? "cu_EURO" : `cu_${smuser.prefer_currency}`;
      updateCurrencyRate(res.data.native_credit);
      updateUserDetails(res.data.response);
      updateAppCurrency(currencyList[key]);
    } catch (e) { console.error(e); }
  }, [smuser, updateCurrencyRate, updateUserDetails, updateAppCurrency]);

  const ChangeCurrencys = useCallback(async (value) => {
    try {
      const res = await instance.post("/changescurrencys", { country: value });
      const currencyList = res.data.currency1;
      const key = value === "EUR" ? "cu_EURO" : `cu_${value}`;
      updateCurrencyRate(currencyList);
      if (res.data.status) { setPreferCurrency(value); setIsClick(true); }
      updateAppCurrency(currencyList[key]);
      setCountryCodes(value);
      localStorage.setItem("PrefCurrency", value);
    } catch (e) { console.error(e); }
  }, [updateCurrencyRate, updateAppCurrency]);

  const UpdatePlatform = useCallback(async (value) => {
    if (!smuser) return;
    try {
      const enc = await apiEncryptRequest({ user_id: smuser.id, status: value, ip: "103:12:1:52" });
      const res = await instance.post("/changesplatforms", enc);
      const data = await decryptData(res.data);
      updateUserDetails(data.user);
      ChangeCurrency(smuser.prefer_currency);
    } catch (e) { console.error(e); }
  }, [smuser, updateUserDetails, ChangeCurrency]);

  const UpdateCountry = useCallback(async (value) => {
    if (!smuser) return;
    try {
      const enc = await apiEncryptRequest({ country: value, user_id: smuser.id, ip: "103.240.168.48", platform_status: smuser.platform_status });
      const res = await instance.post("/changecountry", enc);
      const data = await decryptData(res.data);
      updateUserDetails(data.user);
      window.location.href = "/vm/create";
    } catch (e) { console.error(e); }
  }, [smuser, updateUserDetails]);

  const vmexpires = useCallback(async () => {
    try { const enc = await apiEncryptRequest(); await instance.post("/vmexpires", enc); }
    catch (e) { console.error(e); }
  }, []);

  const getCurrencyByIP = useCallback(async () => {
    try {
      const res = await axios.get("https://ipapi.co/json/");
      const code = res.data.country_code;
      const currency = code === "GB" ? "GBP" : code === "US" ? "USD" : "INR";
      localStorage.removeItem("PrefCurrency");
      setCountryCodes(currency); setPreferCurrency(currency); setIsClick(true);
      localStorage.setItem("PrefCurrency", currency);
      const apiRes = await instance.post("/changescurrencys", { country: currency });
      const currencyList = apiRes.data.currency1;
      const key = currency === "EUR" ? "cu_EURO" : `cu_${currency}`;
      updateCurrencyRate(currencyList);
      updateAppCurrency(currencyList[key]);
    } catch { console.error("Error fetching location data"); }
  }, []);

  // ─── Handlers ─────────────────────────────────────────────────────────────
  const changeCurrency = useCallback((value) => { if (isLogged) ChangeCurrency(value); }, [isLogged, ChangeCurrency]);
  const changePlatform = useCallback((value) => { setPlatform(value); if (isLogged) UpdatePlatform(value); }, [isLogged, UpdatePlatform]);
  const openPopup = useCallback((type) => { setPopUpType(type); setIsPopupOpen((p) => !p); }, []);

  // ─── Effects ──────────────────────────────────────────────────────────────
  useEffect(() => {
    if (smuser && smuser.emailverify !== 1) navigate("/email/verification");
  }, [smuser, navigate]);

  const initUserIdRef = useRef(null);
  useEffect(() => {
    const userId = smuser?.id ?? null;
    if (userId === initUserIdRef.current) return;
    initUserIdRef.current = userId;

    if (smuser) {
      instance.post("/location", { user_id: smuser.id })
        .then((res) => setServerLocation(res.data.location))
        .catch(console.error);
      setPreferCurrency(smuser.prefer_currency);
      setPlatform(smuser.platform_status === 1 ? "1" : "0");
      instance.post("/changescurrency", { country: smuser.prefer_currency, user_id: smuser.id })
        .then((res) => {
          const currencyList = res.data.currency1;
          const key = smuser.prefer_currency === "EUR" ? "cu_EURO" : `cu_${smuser.prefer_currency}`;
          updateCurrencyRate(res.data.native_credit);
          updateUserDetails(res.data.response);
          updateAppCurrency(currencyList[key]);
        }).catch(console.error);
    } else if (!isClick) {
      getCurrencyByIP();
    }
  }, [smuser?.id]);

  useEffect(() => {
    const handleResize = () => setIsMobile(isMobileDevice());
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    const id = setInterval(vmexpires, VM_EXPIRE_INTERVAL);
    return () => clearInterval(id);
  }, [vmexpires]);

  // ─── Shared web select style ───────────────────────────────────────────────
  const webSelectStyle = { borderRadius:"30px", marginRight:"10px", padding:"10px 15px", border:"2px solid #e97730" };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <>
      <style>{HEADER_CSS}</style>

      {isPopupOpen && (
        <div>
          <ProfilePopUp
            type={popUpType}
            isVisible={isPopupOpen}
            wallet={currencyRates}
            upnetcredits={smuser?.reward_points}
            onClose={() => setIsPopupOpen(false)}
          />
        </div>
      )}

      {isMobile ? (
        // ── MOBILE — fluid, no fixed margins, proper touch targets ──────────
        <div className="header-top see-full">
          <div className="hd-mobile">

            {/* Logo — no negative marginLeft */}
            <div className="hd-logo-mobile">
              <a href={isLogged ? "/vm/create" : "/"}>
                <img src="/logo.png" alt="Logo" />
              </a>
            </div>  

            {isLogged ? (
              /* Logged-in controls */
              <div className="hd-controls">
                {/* Currency */}
                <select className="hd-select" value={preferCurrency} onChange={(e) => changeCurrency(e.target.value)}>
                  <CurrencyOptions platform={platform} />
                </select>

                {/* Location */}
                <select className="hd-select" value={selectedLocation} onChange={(e) => setSelectedLocation(e.target.value)}>
                  <option value="Select">GLB</option>
                  {serverLocation?.map((item, i) => (
                    <option key={i} value={item.server_location}>{item.server_location}</option>
                  ))}
                </select>

                {/* Platform */}
                <select className="hd-select" value={platform} onChange={(e) => changePlatform(e.target.value)}>
                  <option value="0">VM</option>
                </select>

                {/* Profile icon */}
                <IconCircle src="/images/menu-icon.png" alt="menu" onClick={() => openPopup("profile")} />

                {/* Wallet icon */}
                <IconCircle src="/user-wallet.png" alt="wallet" onClick={() => openPopup("wallet")} style={{ marginLeft:"2px" }} />
              </div>
            ) : (
              /* Guest — currency selector only, no fixed margin */
              <select
                className="hd-currency-guest"
                onChange={(e) => changeCurrency(e.target.value)}
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
                <option value="AED">AED</option>
              </select>
            )}
          </div>
        </div>
      ) : (
        // ── WEB — unchanged ──────────────────────────────────────────────────
        <>
          {isLogged && smuser ? (
            <div className="header-top see-full">
              <div className="parent-see-width">
                <div className="logo">
                  <a href="/vm/create">
                    <img src="/logo.png" alt="Logo" style={{ marginLeft:"8rem", width:"240px" }} />
                  </a>
                </div>
                <div className="login-main">
                  {isLoginByParentUser == 1 && (
                    <SelectBox value={preferCurrency} onChange={(e) => changeCurrency(e.target.value)} style={webSelectStyle}>
                      <CurrencyOptions platform={platform} />
                    </SelectBox>
                  )}
                  {isLoginByParentUser == 1 && (
                    <SelectBox value={dropdownValue} onChange={(e) => setDropdownValue(e.target.value)} style={webSelectStyle}>
                      <option value="Global">GLOBAL</option>
                      {serverLocation?.map((item, i) => (
                        <option key={i} value={item.server_location}>{item.server_location}</option>
                      ))}
                    </SelectBox>
                  )}
                  {isLoginByParentUser == 1 && (
                    <SelectBox value={platform} onChange={(e) => changePlatform(e.target.value)} style={{ ...webSelectStyle, marginRight:"20px" }}>
                      <option value="0">Native VM</option>
                    </SelectBox>
                  )}

                  <div style={{ display:"flex", alignItems:"center" }}>
                    {isLoginByParentUser == 1 && currencyRates && (
                      <a href="/wallet" className="underline-text" style={{ fontSize:"21px", color:"#035189", marginRight: smuser.reward_points > 0 ? "20px" : "10px" }}>
                        BAL. ({currencyRates})
                      </a>
                    )}
                    {isLoginByParentUser == 1 && smuser.reward_points > 0 && (
                      <a className="underline-text" style={{ fontSize:"21px", color:"#035189", marginRight:"20px" }}>
                        {`UPNETCredits ( ${currencyReturn({ price: smuser.reward_points, symbol: smuser.prefer_currency, rates: appCurrency })} )`}
                      </a>
                    )}
                    <button
                      onClick={() => navigate("/vm-machine")}
                      style={{ marginRight:"8px", fontWeight:"900", color:"white", height:"38px", width:"10rem", backgroundColor:"#e97730", border:"none", borderRadius:"30px", cursor:"pointer" }}
                      onMouseOver={(e) => { e.currentTarget.style.backgroundColor = "#07528B"; }}
                      onMouseOut={(e)  => { e.currentTarget.style.backgroundColor = "#e97730"; }}
                    >My Machine</button>
                    <div className="icons notifications hiddenclass">
                      <a href="/notification">
                        <span className="count-bell" />
                        <div className="bell" style={{ marginRight:"10px", marginTop:"-20px" }}>
                          <img src="/admin/images/admin/common/notification.svg" alt="Notification" />
                        </div>
                      </a>
                    </div>
                    <div style={{ marginRight:"15px", cursor:"pointer", display:"flex", alignItems:"center" }} onClick={() => openPopup("profile")}>
                      <img src="/admin/images/admin/common/admin-icon.png" alt="profile"
                        style={{ borderRadius:"50%", backgroundColor:"#e97730", width:"35px", height:"35px", marginRight:"10px", border:"2px solid white", padding:"5px", outline:"2px solid #e97730" }} />
                      <span style={{ fontSize:"24px", fontWeight:"500", color:"#666", maxWidth:"130px", textOverflow:"ellipsis", whiteSpace:"nowrap", overflow:"hidden", display:"inline-block" }}>
                        {smuser.name}
                      </span>
                      <FaChevronDown style={{ color:"black", marginLeft:"5px" }} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="header-top see-full">
              <div className="parent-see-width">
                <div className="logo">
                  <a href="/">
                    <img src="/logo.png" alt="Logo" style={{ marginLeft:"4rem", width:"260px", marginTop:"1rem" }} />
                  </a>
                </div>
                <div className="login-main">
                  <SelectBox value={countryCodes} onChange={(e) => ChangeCurrencys(e.target.value)} style={webSelectStyle}>
                    {STATIC_CODES.map((code) => (
                      <option key={code} value={code}>{code}</option>
                    ))}
                  </SelectBox>
                  {[{ label:"Sign Up", path:"/signUp" }, { label:"Log in", path:"/login" }].map(({ label, path }) => (
                    <div key={label} className="log-in" onClick={() => navigate(path)}>
                      <a className="media-link">
                        <div className="media">
                          <img className="normal" src="/images/more-info-btn-bg.svg" alt="" />
                          <img className="hover-img" src="/images/more-info-btn-bg.svg" alt="" />
                          <span className="login-text"
                            onMouseOver={(e) => { e.target.style.color = "#07528B"; }}
                            onMouseOut={(e)  => { e.target.style.color = "white"; }}>
                            {label}
                          </span>
                        </div>
                      </a>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default Header;